import styled, { css, keyframes } from 'styled-components';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%; /* Garante que ocupe a tela inteira */
  margin: 0;
  padding: 20px;
  color: var(--text-primary);
`;

export const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  padding: 1.5em 1.25em;
  border-radius: 20px;
  box-shadow: var(--shadow-md);
  z-index: 2;
  background: var(--surface-1);
  border: 1px solid var(--border-subtle);
  width: ${({ collapsed }) => (collapsed ? "80vw" : "70vw")};
  margin: auto;
  margin-top: 50px;
  position: relative;
  overflow: hidden; /* Para evitar vazamentos da imagem */

  @media screen and (max-width: 768px) {
    width: 100% !important;
    max-width: 100%;
    margin-top: 3.25rem;
    padding: 1rem;
    box-sizing: border-box;
  }

  h1 {
    text-align: center;
    align-self: center;
  }

  h3 {
    display: flex;
    align-self: flex-start;
    color: var(--text-heading);
  }

  @media screen and (max-width: 768px) {
    width: 90vw;
  }
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 70%;
  padding: 10px 0;
  gap: 5px;
  margin-bottom: 20px;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin: 1.5rem 0 0.5rem;
  width: 100%;
  box-sizing: border-box;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;

    button {
      width: 100%;
    }
  }
`;

/** Mesmo padrão visual dos botões de navegação (PageHeader / ListAreas). */
export const SettingsActionButton = styled.button`
  background: var(--accent-secondary-soft);
  border: 2px solid var(--border-default);
  border-radius: 16px;
  color: var(--text-heading);
  cursor: pointer;
  padding: 10px 20px;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 14px;
  font-weight: 500;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  min-height: 42px;
  min-width: 140px;
  white-space: nowrap;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      var(--accent-secondary-soft) 0%,
      var(--accent-soft) 100%
    );
    opacity: 0;
    transition: opacity 0.3s ease;
    border-radius: 14px;
    z-index: -1;
  }

  &:hover {
    transform: translateY(-2px);
    border-color: var(--accent);
    color: var(--text-primary);

    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(-1px);
  }
`;

/**
 * Deslogar: mesmo layout/tamanho dos demais, variante destrutiva.
 */
export const SettingsLogoutButton = styled(SettingsActionButton)`
  background: color-mix(in srgb, var(--danger) 18%, var(--surface-2));
  border-color: color-mix(in srgb, var(--danger) 55%, var(--border-default));
  color: var(--danger);

  &::before {
    background: linear-gradient(
      135deg,
      color-mix(in srgb, var(--danger) 22%, transparent) 0%,
      color-mix(in srgb, var(--danger) 12%, transparent) 100%
    );
  }

  &:hover {
    border-color: var(--danger);
    color: var(--danger);
    background: color-mix(in srgb, var(--danger) 28%, var(--surface-2));
  }
`;

// Adicione este novo componente styled para o container do título
export const SectionContainer = styled.div`
  width: 100%;
  text-align: center;
  margin: 40px 0;

  h2 {
    color: var(--text-heading);
  }
`;

const themeGlow = keyframes`
  0%, 100% {
    box-shadow:
      var(--shadow-sm),
      0 0 0 2px color-mix(in srgb, var(--accent-secondary) 35%, transparent),
      0 0 18px var(--accent-soft);
    border-color: var(--text-heading);
  }
  50% {
    box-shadow:
      var(--shadow-md),
      0 0 0 3px var(--border-strong),
      0 0 28px color-mix(in srgb, var(--accent-secondary) 55%, transparent);
    border-color: var(--accent-secondary);
  }
`;

const themeNudge = keyframes`
  0%, 82%, 100% { transform: translateX(0); }
  86% { transform: translateX(-4px); }
  90% { transform: translateX(4px); }
  94% { transform: translateX(-3px); }
  98% { transform: translateX(2px); }
`;

const tipBounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

export const ThemeSection = styled.section`
  width: 100%;
  align-self: stretch;
  box-sizing: border-box;
  margin: 0.75rem 0;
  padding: 1.25rem 1.4rem;
  border: 1px solid var(--border-default);
  border-radius: 16px;
  background: var(--surface-2);
  box-shadow: var(--shadow-sm);
  position: relative;

  ${({ $highlight }) =>
    $highlight &&
    css`
      border-color: var(--text-heading);
      background: color-mix(in srgb, var(--accent-secondary-soft) 70%, var(--surface-2));
      animation:
        ${themeGlow} 2.2s ease-in-out infinite,
        ${themeNudge} 4.5s ease-in-out infinite;
    `}
`;

/** Linha do toggle: texto à esquerda, switch sempre na mesma margem direita. */
export const PreferenceToggleRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: ${({ $flush }) => ($flush ? "0" : "0.85rem")};
  box-sizing: border-box;

  > label {
    width: 100%;
    margin: 0;
  }
`;

/** Terceiro balão — navegação (ex.: Controle de Versão Guardian). */
export const NavBalloon = styled.button`
  width: 100%;
  align-self: stretch;
  box-sizing: border-box;
  margin: 0.75rem 0;
  padding: 1.25rem 1.4rem;
  border: 1px solid var(--border-default);
  border-radius: 16px;
  background: var(--surface-2);
  box-shadow: var(--shadow-sm);
  color: var(--text-heading);
  font-size: 1.05rem;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background: color-mix(in srgb, var(--surface-3) 85%, #000);
    border-color: var(--border-strong);
    box-shadow: var(--shadow-md);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

export const ThemeDescription = styled.p`
  margin: 0.65rem 0 0;
  color: var(--text-secondary);
  font-size: 0.86rem;
  line-height: 1.45;
  text-align: left;
`;

/** Mesmo card visual do tema — preferências genéricas (ex.: FIRMS). */
export const PreferenceSection = styled(ThemeSection)`
  animation: none;
`;

export const PreferenceTitle = styled.h2`
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text-heading);
  text-align: left;
`;

export const ThemeTip = styled.div`
  display: inline-flex;
  align-items: flex-start;
  gap: 0.4rem;
  margin-top: 0.85rem;
  max-width: 100%;
  padding: 0.5rem 0.7rem;
  border-radius: 10px;
  border: 1px solid var(--border-default);
  background: var(--accent-soft);
  color: var(--text-heading);
  font-size: 12px;
  line-height: 1.4;
  animation: ${tipBounce} 1.8s ease-in-out infinite;
  text-align: left;

  strong {
    color: var(--accent-secondary);
  }
`;

export const ThemeTipPointer = styled.span`
  flex-shrink: 0;
  font-size: 14px;
  line-height: 1.2;
`;

export const ThemeTipDismiss = styled.button`
  margin-left: auto;
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  padding: 0.15rem 0.35rem;
  border-radius: 6px;
  white-space: nowrap;

  &:hover {
    color: var(--text-heading);
    background: var(--surface-glass);
  }
`;

export const NewFeatureBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 0.45rem;
  padding: 0.12rem 0.45rem;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-on-accent);
  background: linear-gradient(90deg, var(--accent), var(--accent-secondary));
  box-shadow: 0 0 12px var(--accent-soft);
`;
